import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Skeleton} from '@/components/ui/skeleton'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Tag} from '@/components/ui/tag'
import {KairosMark} from '@/components/kairos-mark'
import {PorQue} from '@/components/ui/por-que'
import {useDebouncedValue} from '@/hooks/common/use-debounced-value'
import {
  ATTENTION_PAGE_SIZE,
  useAttention,
  type AttentionBucket,
  type AttentionBusiness,
  type AttentionSignal,
} from '@/hooks/inci/consola/use-admin'
import {signalRow, signalTone} from '@/lib/attention'
import {BAND_TONE} from '@/lib/bands'
import {RISK_CLASS_ROMAN, RISK_CLASS_TONE, formatRatePct} from '@/lib/risk'
import {tope} from '@/lib/campos-texto'

const BUCKET_ORDER: AttentionBucket[] = ['critico_ahora', 'esta_semana', 'pronto']

const BUCKET_SWATCH: Record<AttentionBucket, string> = {
  critico_ahora: 'swatch-red',
  esta_semana: 'swatch-amber',
  pronto: 'swatch-neutral',
}

const BUCKET_COUNT_TONE: Record<AttentionBucket, 'red' | 'amber' | 'neutral'> = {
  critico_ahora: 'red',
  esta_semana: 'amber',
  pronto: 'neutral',
}

// Espeja uno a uno `SIGNAL_BUCKET` de `core/attention.py`.
const BUCKET_HINT_KEY: Record<AttentionBucket, string> = {
  critico_ahora: 'admin.worklist.criticoAhoraHint',
  esta_semana: 'admin.worklist.estaSemanaHint',
  pronto: 'admin.worklist.prontoHint',
}

function SignalChip({signal}: {signal: AttentionSignal}) {
  const {t} = useTranslation()
  const tone = signalTone(signal.bucket)
  const {label, detail} = signalRow(t, signal)
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11.5px] font-medium leading-tight ${tone === 'red' ? 'tone-red' : 'tone-amber'}`}>
      <span className="size-1.5 rounded-full bg-current opacity-85" />
      {label}
      {detail && <span className="font-mono text-[10.5px] opacity-80">· {detail}</span>}
    </span>
  )
}

function WorklistRow({business, onOpen}: {business: AttentionBusiness; onOpen: (id: string, name: string) => void}) {
  const {t} = useTranslation()
  const hasAssessment = business.score_pct != null && business.band != null
  return (
    <div
      data-testid="worklist-row"
      className="surface-divider grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-4 border-b px-4 py-3.5 transition-colors last:border-b-0 hover:bg-muted/45">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-foreground">{business.name}</span>
          <span className="font-mono text-[11.5px] text-muted-foreground">{business.nit}</span>
          {business.risk_class != null ? (
            <Tag tone={RISK_CLASS_TONE[business.risk_class]}>
              {t('business.risk.classValue', {roman: RISK_CLASS_ROMAN[business.risk_class]})}
            </Tag>
          ) : (
            <Tag tone="neutral">{t('admin.worklist.noRisk')}</Tag>
          )}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {business.signals.map((signal) => (
            <SignalChip key={signal.kind} signal={signal} />
          ))}
        </div>
      </div>
      <div className="flex flex-col items-end gap-0.5 whitespace-nowrap text-right">
        {hasAssessment ? (
          <>
            <span className="flex items-center justify-end gap-1.5">
              <span className="font-mono text-[13px] font-medium">{formatRatePct(business.score_pct!)}</span>
              <Tag tone={BAND_TONE[business.band!]}>{t(`sgsst.bands.${business.band}.label`)}</Tag>
            </span>
            <span className="font-mono text-[11px] text-muted-foreground">
              {t('admin.worklist.support', {
                supported: business.cumple_con_soporte ?? 0,
                total: business.cumple_total ?? 0,
              })}
            </span>
          </>
        ) : (
          <>
            <span className="font-mono text-[13px] text-muted-foreground">{t('admin.worklist.noData')}</span>
            <span className="font-mono text-[11px] text-muted-foreground">{t('admin.worklist.startup')}</span>
          </>
        )}
      </div>
      <Button variant="outline" size="sm" onClick={() => onOpen(business.business_id, business.name)}>
        {t('admin.worklist.open')} <span aria-hidden>→</span>
      </Button>
    </div>
  )
}

function Bucket({
  bucket,
  businesses,
  onOpen,
}: {
  bucket: AttentionBucket
  businesses: AttentionBusiness[]
  onOpen: (id: string, name: string) => void
}) {
  const {t} = useTranslation()
  return (
    <div className="flex flex-col gap-2" data-testid={`bucket-${bucket}`}>
      <div className="flex items-center gap-2 px-0.5">
        <span className={`size-2.5 rounded-sm ${BUCKET_SWATCH[bucket]}`} />
        <span className="font-heading text-[15px] font-semibold">{t(`admin.worklist.buckets.${bucket}`)}</span>
        <Tag tone={BUCKET_COUNT_TONE[bucket]}>{businesses.length}</Tag>
        {/* La ayuda de «pronto» cita el umbral `SIN_SOPORTE_MIN_CUMPLE` de `core/attention.py`. */}
        {/* T11: el rótulo nombra su grupo; tres «Ver ayuda» iguales serían botones mudos. */}
        <PorQue
          label={t('admin.worklist.criterioLabel')}
          ariaLabel={t('admin.worklist.criterioAria', {grupo: t(`admin.worklist.buckets.${bucket}`)})}
          testId={`worklist-${bucket}-help`}
          contenidoTestId={`worklist-${bucket}-criterio`}>
          {t(BUCKET_HINT_KEY[bucket])}
        </PorQue>
      </div>
      {businesses.length === 0 ? (
        <div className="surface px-4 py-4 text-[13.5px] text-muted-foreground">{t('admin.worklist.emptyBucket')}</div>
      ) : (
        <div className="surface">
          {businesses.map((b) => (
            <WorklistRow key={b.business_id} business={b} onOpen={onOpen} />
          ))}
        </div>
      )}
    </div>
  )
}

/** Agrupa por urgencia dentro de la página: el orden del servidor ya es (bucket, severidad, nombre, id). */
export function AttentionWorklist({onOpen}: {onOpen: (id: string, name: string) => void}) {
  const {t} = useTranslation()
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const search = useDebouncedValue(searchInput, 300)

  function updateSearch(value: string) {
    setSearchInput(value)
    setPage(1)
  }

  const {data, isLoading} = useAttention({page, search}, true)
  const total = data?.total ?? 0
  const items = data?.items ?? []
  const buckets = BUCKET_ORDER.map((bucket) => ({
    bucket,
    businesses: items.filter((b) => b.bucket === bucket),
  }))

  return (
    <div className="space-y-4" data-testid="attention-worklist">
      <div className="flex items-baseline gap-2.5">
        <h2 className="font-heading text-lg font-semibold text-foreground">{t('admin.worklist.title')}</h2>
        <span className="text-[13px] text-muted-foreground">
          {total === 0 ? t('admin.worklist.subtitleClear') : t('admin.worklist.subtitle', {count: total})}
        </span>
      </div>
      <Input
        maxLength={tope('BUSCA')}
        value={searchInput}
        onChange={(e) => updateSearch(e.target.value)}
        placeholder={t('admin.worklist.searchPlaceholder')}
        className="max-w-xs"
      />
      {isLoading && !data ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <>
          {total === 0 && (
            <div className="flex items-center gap-2.5 rounded-xl bg-accent px-4 py-3.5 text-[13.5px] text-accent-foreground">
              <KairosMark />
              {t('admin.worklist.allClear')}
            </div>
          )}
          {buckets.map((b) => (
            <Bucket key={b.bucket} bucket={b.bucket} businesses={b.businesses} onOpen={onOpen} />
          ))}
          {total > ATTENTION_PAGE_SIZE && (
            <PaginationBar page={page} pageSize={ATTENTION_PAGE_SIZE} total={total} onPageChange={setPage} />
          )}
        </>
      )}
    </div>
  )
}
